import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import Navbar from "@/components/Navbar";
import TopUrgencyBar from "@/components/TopUrgencyBar";
import Footer from "@/components/Footer";
import SponsorForm from "@/components/SponsorForm";
import { SPONSOR_TIERS } from "@/lib/sponsorTiers";
import { Handshake, Users, Award, CheckCircle2, ArrowRight } from "lucide-react";

export default function Sponsor() {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const initialTier = params.get("tier") || "";
  const [pickedTier, setPickedTier] = useState(initialTier);

  useEffect(() => {
    if (initialTier) setPickedTier(initialTier);
  }, [initialTier]);

  const scrollToForm = (slug) => {
    if (slug) setPickedTier(slug);
    const el = document.getElementById("sponsor-form");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="min-h-screen bg-[#FAFAF7]" data-testid="sponsor-page">
      <TopUrgencyBar />
      <Navbar onSaveSpot={() => scrollToForm()} />

      {/* Hero */}
      <header className="relative bg-[#0A1128] text-white overflow-hidden pt-[124px] md:pt-[128px]">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-br from-[#0A1128] via-[#0F1B3D] to-[#0A1128]" />
          <div className="absolute inset-0 opacity-[0.05]" style={{ backgroundImage: "linear-gradient(rgba(255,255,255,.15) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.15) 1px, transparent 1px)", backgroundSize: "60px 60px" }} />
          <div className="absolute top-10 left-10 w-80 h-80 bg-[#FACC15]/10 rounded-full blur-3xl pointer-events-none" />
          <div className="absolute bottom-10 right-0 w-96 h-96 bg-[#0066FF]/15 rounded-full blur-3xl pointer-events-none" />
        </div>
        <div className="relative max-w-5xl mx-auto px-6 lg:px-10 py-14 md:py-20 text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-[#FACC15]/10 border border-[#FACC15]/30 text-[#FACC15] rounded-full mb-5 text-[10px] md:text-xs font-black uppercase tracking-[0.25em]">
            <Handshake size={11} /> Sponsorship · Oct 1–4, 2026
          </div>
          <h1 className="font-display text-4xl sm:text-5xl md:text-6xl leading-[0.95] mb-4">
            Get in front of the people who
            <br />
            <span className="relative inline-block">
              <span className="relative z-10">actually run swim schools.</span>
              <span className="absolute inset-x-0 bottom-1 h-4 bg-[#FACC15]/90 -z-0" />
            </span>
          </h1>
          <p className="text-lg md:text-xl text-white/85 max-w-2xl mx-auto leading-relaxed mb-8">
            SSU Workshops brings 40+ independent swim-school owners and lead instructors together for four days at a private estate in West Palm Beach. They are the buyers — not the influencers, not the tire-kickers. Sponsorship puts your brand directly in the room.
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            <button
              type="button"
              onClick={() => scrollToForm()}
              data-testid="sponsor-hero-cta"
              className="btn-shine inline-flex items-center gap-2 bg-[#FACC15] hover:bg-[#EAB308] text-[#0A1128] font-black tracking-widest uppercase px-6 py-3 rounded-md transition-colors text-sm"
            >
              Apply now <ArrowRight size={16} />
            </button>
            <a
              href="#tiers"
              data-testid="sponsor-hero-view-tiers"
              className="inline-flex items-center gap-2 border-2 border-white/20 hover:border-[#FACC15] text-white font-black tracking-widest uppercase px-6 py-3 rounded-md transition-colors text-sm"
            >
              View tiers
            </a>
          </div>

          {/* Trust bar */}
          <div className="mt-12 grid grid-cols-3 gap-4 max-w-2xl mx-auto text-center">
            <div className="border-l-2 border-[#FACC15]/40 pl-3 md:pl-4">
              <div className="font-display text-3xl md:text-4xl text-[#FACC15]">40+</div>
              <div className="text-[10px] md:text-xs uppercase tracking-widest text-white/60 mt-1">Swim school owners</div>
            </div>
            <div className="border-l-2 border-[#FACC15]/40 pl-3 md:pl-4">
              <div className="font-display text-3xl md:text-4xl text-[#FACC15]">4</div>
              <div className="text-[10px] md:text-xs uppercase tracking-widest text-white/60 mt-1">Days on property</div>
            </div>
            <div className="border-l-2 border-[#FACC15]/40 pl-3 md:pl-4">
              <div className="font-display text-3xl md:text-4xl text-[#FACC15]">1</div>
              <div className="text-[10px] md:text-xs uppercase tracking-widest text-white/60 mt-1">Room. No noise.</div>
            </div>
          </div>
        </div>
        <div className="absolute bottom-0 inset-x-0 h-8 bg-[#FAFAF7]" style={{ clipPath: "polygon(0 100%, 100% 100%, 100% 0)" }} />
      </header>

      {/* Why sponsor */}
      <section className="max-w-6xl mx-auto px-6 lg:px-10 py-16 md:py-20">
        <div className="max-w-3xl mb-10">
          <div className="text-xs font-black tracking-[0.25em] text-[#0066FF] uppercase mb-3">Why this room</div>
          <h2 className="font-display text-3xl md:text-5xl text-[#0A1128] leading-[1.05] mb-4">
            A conference doesn't sell your product.<br />A community does.
          </h2>
          <p className="text-base md:text-lg text-[#374151] leading-relaxed">
            Traditional swim expos are noisy, transactional, and priced for scale — you pay to shout at 1,000 people who came for something else. SSU Workshops is the opposite: a small, high-context room where you get real conversations with the buyers who make the calls, then a full weekend of goodwill after.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-5">
          {[
            { icon: Users, title: "The buyer is in the room", body: "Every attendee owns or leads a swim school. No students, no hobbyists, no gatekeepers." },
            { icon: Handshake, title: "Four days, not four minutes", body: "You're not stuck at a table. You're on the property, at meals, in conversations — brand as friend, not vendor." },
            { icon: Award, title: "One partner per category", body: "Title-level partners get category exclusivity. If you're in, your competitor isn't." },
          ].map((b) => {
            const Icon = b.icon;
            return (
              <div key={b.title} className="bg-white border-2 border-[#E5E7EB] hover:border-[#FACC15] transition-colors rounded-2xl p-6">
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-[#FACC15]/15 text-[#A16207] mb-4">
                  <Icon size={22} />
                </div>
                <div className="font-display-tight text-xl text-[#0A1128] mb-2">{b.title}</div>
                <div className="text-sm text-[#374151] leading-relaxed">{b.body}</div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Tiers */}
      <section id="tiers" className="bg-white border-y border-[#E5E7EB]">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 py-16 md:py-20">
          <div className="max-w-3xl mb-10">
            <div className="text-xs font-black tracking-[0.25em] text-[#0066FF] uppercase mb-3">The Tiers</div>
            <h2 className="font-display text-3xl md:text-5xl text-[#0A1128] leading-[1.05] mb-3">
              Three ways to show up.
            </h2>
            <p className="text-base md:text-lg text-[#374151] leading-relaxed">
              Pick the level that matches your goals. Every tier gets a real seat at the table — this isn't a logo-on-a-lanyard situation.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {SPONSOR_TIERS.map((t, i) => {
              const isTitle = t.slug === "title-partner";
              return (
                <div
                  key={t.slug}
                  data-testid={`sponsor-tier-card-${t.slug}`}
                  className={`relative flex flex-col rounded-2xl border-2 p-7 transition-all ${
                    isTitle
                      ? "bg-[#0A1128] text-white border-[#FACC15] shadow-[0_30px_60px_-30px_rgba(250,204,21,0.5)]"
                      : "bg-white border-[#E5E7EB] hover:border-[#0066FF]"
                  }`}
                >
                  {isTitle && (
                    <span className="absolute -top-3 left-6 inline-flex items-center gap-1 bg-[#FACC15] text-[#0A1128] text-[10px] font-black uppercase tracking-widest px-3 py-1 rounded-full">
                      ★ Most Visible
                    </span>
                  )}
                  <div className={`text-[10px] font-black uppercase tracking-[0.25em] mb-2 ${isTitle ? "text-[#FACC15]" : "text-[#0066FF]"}`}>
                    {t.spotsLabel}
                  </div>
                  <div className={`font-display text-3xl md:text-4xl mb-1 leading-tight ${isTitle ? "text-white" : "text-[#0A1128]"}`}>
                    {t.label}
                  </div>
                  <div className={`font-display-tight text-2xl mb-4 ${isTitle ? "text-[#FACC15]" : "text-[#0066FF]"}`}>
                    {t.price}
                  </div>
                  <p className={`text-sm mb-5 leading-relaxed ${isTitle ? "text-white/85" : "text-[#374151]"}`}>
                    {t.tagline}
                  </p>
                  <ul className={`space-y-2.5 mb-6 flex-1 ${isTitle ? "text-white/90" : "text-[#0A1128]"}`}>
                    {t.perks.map((perk) => (
                      <li key={perk} className="flex items-start gap-2 text-sm leading-snug">
                        <CheckCircle2 size={16} className={`mt-0.5 shrink-0 ${isTitle ? "text-[#FACC15]" : "text-emerald-600"}`} />
                        <span>{perk}</span>
                      </li>
                    ))}
                  </ul>
                  <button
                    type="button"
                    onClick={() => scrollToForm(t.slug)}
                    data-testid={`sponsor-tier-apply-${t.slug}`}
                    className={`mt-auto inline-flex items-center justify-center gap-2 rounded-md font-black tracking-widest uppercase text-sm px-5 py-3 transition-colors ${
                      isTitle
                        ? "bg-[#FACC15] hover:bg-[#EAB308] text-[#0A1128]"
                        : "bg-[#0A1128] hover:bg-[#0066FF] text-white"
                    }`}
                  >
                    Apply for {t.label} <ArrowRight size={14} />
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Form */}
      <section id="sponsor-form" className="max-w-4xl mx-auto px-6 lg:px-10 py-16 md:py-20 scroll-mt-32">
        <div className="max-w-2xl mb-8">
          <div className="text-xs font-black tracking-[0.25em] text-[#0066FF] uppercase mb-3">Apply</div>
          <h2 className="font-display text-3xl md:text-5xl text-[#0A1128] leading-[1.05] mb-3">
            Tell us about your brand.
          </h2>
          <p className="text-base md:text-lg text-[#374151] leading-relaxed">
            This is a real application, not a checkout. Once you submit, our team will follow up within 2–3 business days to walk through the fit, logistics, and next steps. No auto-invoice, no gotcha.
          </p>
        </div>
        <SponsorForm initialTier={pickedTier} />
      </section>

      <Footer />
    </div>
  );
}
